import type { CourseSummary } from '../api/client.js';
import { selectAuthoringCourseKey } from './courseDefaults.js';
import { readCourseParam } from './courseParam.js';
import { buildCourseBrowserPath } from './authorNavigation.js';
import { sessionStore } from './browserStorage.js';

/**
 * Which CFA exam level is this screen about, who said so, and where does changing it land?
 *
 * ## Why this exists
 *
 * The Academic Director reviews Level I today. Asked how he wanted Levels II and III surfaced, he
 * answered (2026-08-27): *"three queues, one level at a time would be ideal … there are topics that
 * are similar but not tested between levels and I wouldn't want to get my wires crossed upon review
 * as the lines blur."*
 *
 * That is not a request for a filter. A Level II treatment of a topic and a Level I treatment of the
 * same topic **look alike and are graded differently**, so reviewing one while believing it is the
 * other produces a confidently wrong decision. The requirement is therefore that the level on screen
 * is never in doubt and never wrong — which makes "did anyone actually SAY which level this is?" a
 * question the code has to be able to answer, not just "which level shall we show?".
 *
 * ## What is deliberately not here
 *
 * **A level is never derived from a course id.** `20b` is Level I (the 2024 vintage), `350` is Level
 * II, `400` is Level III, and `450` is Level II (2022). The level is a declared field on the API's
 * catalog (`packages/api/src/common/cfa-courses.ts`); {@link courseLevelLabel} reads it and answers
 * `null` when it is absent rather than guessing.
 *
 * **The grant check is inherited, not re-implemented.** Every branch of
 * {@link resolveCourseSelection} resolves through `selectAuthoringCourseKey`, which returns only a
 * member of the granted list. A second membership test written here would be a second thing to keep
 * in step with the server's `ContentService.listCourses`.
 */

export type CourseSelectionSource = 'url' | 'remembered' | 'default';

export interface CourseSelection {
  /** The course in force. `''` when the granted catalog has not arrived yet — never a guess. */
  readonly courseKey: string;
  /**
   * How that course was chosen. `'url'` and `'remembered'` mean somebody chose it; `'default'` means
   * the application picked one because nothing said otherwise. The distinction is load-bearing: a
   * screen about ONE piece of content must not announce a level nobody declared.
   */
  readonly source: CourseSelectionSource;
}

/** Where the reviewer's chosen level is kept between visits. */
const REMEMBERED_COURSE_STORAGE_KEY = 'author.review.courseKey';

/**
 * `sessionStorage`, and the choice is deliberate twice over.
 *
 * **Per tab is the SAFER semantics here.** Two tabs open on two levels is precisely the situation the
 * reviewer described — similar topics, different levels, wires crossed. A per-tab memory cannot let
 * one tab's level decide what another tab opens; a shared `localStorage` value could, and would do it
 * asynchronously, from a `storage` event nobody is watching.
 *
 * **And `localStorage` is not testable in this repo's environment.** Under `vitest` + `jsdom` as
 * configured here (`packages/web/vite.config.ts` → `test.environment: 'jsdom'`),
 * `window.localStorage` is an empty null-prototype object: `typeof localStorage` is `'object'`,
 * `localStorage.setItem` is `undefined`, and every call throws `TypeError`. Because the accessors
 * below are wrapped in `try/catch` — correctly, since a private window throws for real — a
 * `localStorage`-backed memory would fail SILENTLY in every test, and every test of the remembered
 * branch would be a *cannot-pass* check dressed as a passing one. `sessionStorage` works in the same
 * environment, so the branch is genuinely exercised. Measured 2026-08-28.
 *
 * Losing the value costs one click and never costs correctness: the URL is checked first, so the
 * worst a missing or stale value can do is decide what a *bare* `/queue` opens.
 */
function storage(): Storage | null {
  // Shape-checked as well as `try`-wrapped, for the two separate reasons `lib/browserStorage.ts`
  // records. That rule lives in one module now: it used to be here and NOT in the older
  // `lib/authorNavigation.ts`, which reached for `sessionStorage` bare and would have taken the
  // Courses screen down in a storage-blocked browser.
  return sessionStore();
}

export function readRememberedCourse(): string | null {
  try {
    const raw = storage()?.getItem(REMEMBERED_COURSE_STORAGE_KEY);
    return raw && raw.trim() !== '' ? raw : null;
  } catch {
    return null;
  }
}

export function rememberCourse(courseKey: string): void {
  try {
    if (!courseKey || courseKey.trim() === '') return;
    storage()?.setItem(REMEMBERED_COURSE_STORAGE_KEY, courseKey);
  } catch {
    /* storage is a convenience; losing it costs one click, and throwing would cost the screen */
  }
}

export function forgetRememberedCourse(): void {
  try {
    storage()?.removeItem(REMEMBERED_COURSE_STORAGE_KEY);
  } catch {
    /* see above */
  }
}

/**
 * The course in force, in one precedence, used by every course-scoped screen.
 *
 * 1. **The URL wins, always.** A deep link opens the level it names, so a bookmarked queue and a
 *    link somebody sends both mean exactly what they say.
 * 2. Otherwise the **remembered** course, if this session still holds a grant for it.
 * 3. Otherwise the existing authoring default (`preferredAuthoring`, then `12b`/`10b`, then first).
 *
 * A requested or remembered course the session cannot open falls through — and is reported as
 * `'default'`, not as a choice, because nobody's choice survived.
 */
export function resolveCourseSelection(
  coursesOrIds: Array<CourseSummary | string>,
  requested: string | null | undefined,
): CourseSelection {
  const url = requested && requested.trim() !== '' ? requested : null;
  if (url && selectAuthoringCourseKey(coursesOrIds, url) === url) {
    return { courseKey: url, source: 'url' };
  }

  const remembered = readRememberedCourse();
  if (remembered && selectAuthoringCourseKey(coursesOrIds, remembered) === remembered) {
    return { courseKey: remembered, source: 'remembered' };
  }

  return { courseKey: selectAuthoringCourseKey(coursesOrIds), source: 'default' };
}

/** The declared exam level, or `null`. Never inferred from the id — see the module comment. */
export function courseLevelLabel(course: CourseSummary | undefined | null): string | null {
  return course?.level ?? null;
}

/**
 * The course a LOCATION names, whether it names it in the query string or in the path.
 *
 * **On a `/courses/:courseId/…` route the PATH wins, and this is not a stylistic preference.**
 * `LessonTranscriptPage` and `EntityEditor` load their content from the path segment and ignore the
 * query entirely, so `/courses/400/entities/1448/transcript?courseKey=12b` renders a Level III lesson.
 * A header built from the query would announce "Level I" over it — and the editor route can save, so
 * that combination is a Level III write under a Level I heading. Whatever loads the content is what
 * the header must name.
 *
 * Everywhere else the query is the only thing that names a course, under either spelling
 * (`?courseKey` canonical, `?courseId` the course browser's legacy alias — see `lib/courseParam.ts`).
 */
export function readCourseFromLocation(
  pathname: string,
  params: { get(name: string): string | null },
): string | null {
  const match = /^\/courses\/([^/]+)\//.exec(pathname);
  if (match?.[1]) {
    try {
      return decodeURIComponent(match[1]);
    } catch {
      return match[1];
    }
  }

  return readCourseParam(params);
}

/**
 * Is this screen about ONE piece of content, rather than a course-wide list?
 *
 * Two consumers, and they must agree:
 *
 * - the header, which must not announce a level nobody declared while a single lesson is on screen;
 * - the header again, which **offers no level control at all here**. A review screen can hold unsaved
 *   comments, ratings, rejection reasons, follow-up tags and player edits, and `lib/unsavedWork.ts`
 *   is a probe registry rather than a React Router navigation blocker — so a shell control that
 *   navigates away would discard that work with no prompt. Changing level is done from the queue.
 */
export function isItemScopedPath(pathname: string): boolean {
  return (
    /^\/queue\/(lesson|practice)\/[^/]+/.test(pathname)
    || /^\/courses\/[^/]+\/(entities|tasks)\/[^/]+/.test(pathname)
  );
}

/**
 * Which parameters survive a level switch, PER ROUTE.
 *
 * A single global list was wrong: `taskType` and `priority` are meaningful on `/queue` and ignored by
 * `/board`, which reads only `status` (`WorkQueueBoard.tsx` `boardStatusesFromParam`). Keeping a
 * parameter the destination ignores puts a filter in the address bar that nothing honours.
 *
 * Everything NOT listed is dropped, and the ones worth naming are `sectionId`, `clusterKey`,
 * `waveId`, `runId`, `groupId` and `imported` — each addresses a place inside the course being left,
 * and section 25 of Level I is a different section from section 25 of Level III.
 *
 * **`search` is deliberately dropped**, against the reviewer's advice on this point: `taskType` names
 * a kind of work, but a search string is words that occurred in the OTHER level's content. Carried
 * over, it hides items in the new level for a reason the reviewer would have to remember typing.
 */
const KEPT_PARAMS_BY_ROUTE: Record<string, readonly string[]> = {
  '/queue': ['status', 'taskType', 'priority'],
  '/board': ['status'],
};

/**
 * Where the level selector navigates. A route table, not a generic rule.
 *
 * - **`/queue`, `/board`** stay put and re-scope, keeping only what that route actually reads.
 * - **`/courses` and any `/courses/<id>/…`** hand over to the course browser, in the browser's own
 *   `?courseId` spelling (see `lib/courseParam.ts` — this app spells the question two ways and only
 *   one half writes it).
 * - **`/queue/structural`** leaves for the queue when the destination course does not support
 *   structural review at all. `350`, `400` and `450` all have `supportsStructuralWorkflow: false`
 *   (`common/cfa-courses.ts`), so switching to Level II or III and staying would land the reviewer on
 *   a screen that can only tell him it is unavailable.
 * - **`/audit`** leaves for the queue unconditionally. The audit log is not course-scoped in any way
 *   — `AuditLogPage` fetches the latest events across every course — so `?courseKey=400` above it
 *   would put a Level III badge over Level I rows. There is no honest way to re-scope it, so the
 *   switch takes the reviewer somewhere that is scoped.
 * - **Anything unrecognised** leaves for the queue too, deliberately: an unknown screen is one whose
 *   scoping nobody has thought about, and guessing that it re-scopes on `?courseKey` is how `/audit`
 *   would have acquired a lying badge.
 *
 * `supportsStructural` is passed in rather than looked up, because the level is DECLARED data the
 * caller already holds and this file must not acquire its own copy of the catalog.
 */
export function buildCourseSwitchTarget(
  pathname: string,
  params: URLSearchParams,
  nextCourseKey: string,
  options: { supportsStructural?: boolean } = {},
): string {
  // Refuse rather than emit `?courseKey=`, which reads as "explicitly no course".
  if (!nextCourseKey || nextCourseKey.trim() === '') return pathname;

  if (pathname === '/courses' || /^\/courses\/[^/]+/.test(pathname)) {
    return buildCourseBrowserPath(nextCourseKey);
  }

  const structuralStaysPut = pathname === '/queue/structural' && options.supportsStructural === true;
  const destination = KEPT_PARAMS_BY_ROUTE[pathname] ? pathname : structuralStaysPut ? pathname : '/queue';

  const next = new URLSearchParams();
  next.set('courseKey', nextCourseKey);
  for (const name of KEPT_PARAMS_BY_ROUTE[destination] ?? []) {
    const value = params.get(name);
    if (value) next.set(name, value);
  }

  return `${destination}?${next.toString()}`;
}

/**
 * WHAT THE APPLICATION SUBSTITUTED, AND WHETHER IT IS STILL TRUE — kept as a pair, not re-derived.
 *
 * When a link names a course this session holds no grant for, {@link resolveCourseSelection} falls
 * through to one it can open, and the header says so, naming both. That notice was DEAD on the work
 * queue — the screen the reviewer lives on — while working on the board and the release-waves
 * screens. Measured in a real browser at 80, 200, 500 and 1200 ms after commit: nothing at any of
 * them. The cause is not in the notice: `WorkQueueDashboard` reconciles the unauthorised course
 * against the catalog and then writes the corrected filters back to the address bar, so **the
 * evidence of the substitution is erased by the screen that performed it**, and a header comparing
 * the live location against the course in force has nothing left to report. The board and the waves
 * screen do not rewrite their URL, which is exactly why it survived there.
 *
 * So the pair is carried forward across the rewrite. Both halves are stored because both are what
 * the sentence on screen claims — *"this link asked for 350, which you cannot open; showing 12b"* —
 * and a retained `requested` beside a changed `shown` would be a true half and a false half.
 *
 * ## The retention contract, stated rather than implied
 *
 * **It is kept while the substitution it describes is still in force, and dropped the moment
 * anything supersedes it.** Concretely:
 *
 * - A `PUSH` or `POP` is a NEW request — the reviewer chose a level, followed a link, or went back —
 *   and supersedes the old one. Dropped.
 * - A `REPLACE` that leaves the same course in force is a screen normalising its own address bar.
 *   Kept. **This deliberately includes a reviewer changing a filter on the substituted queue**: he
 *   is still looking at `12b` because his link for `350` could not be opened, and that is still worth
 *   saying. The alternative reading — that any user action clears it — would make the notice vanish
 *   on the first click, which is the behaviour we are fixing rather than a different one.
 * - A `REPLACE` that changes the course in force is a real change of level by some other route (the
 *   course browser's own picker does exactly this). Dropped.
 *
 * ## Why a pure function called during render
 *
 * The comparison has to be made at RENDER time. An effect is one frame late, and one frame late is
 * precisely the defect this file already fixed once in the structural badge. The caller therefore
 * applies this during render through React's sanctioned "adjust state when something changes"
 * pattern — `setState` during render, whose result is DISCARDED if the render is aborted. That is
 * the difference that matters versus a `useRef`: a ref mutated during a speculative render survives
 * the abort and can leak a notice from a navigation that never committed.
 *
 * **It must be idempotent**, or `setState`-during-render loops: when nothing has changed it returns
 * the caller's own object, by reference.
 */
export interface CourseSubstitution {
  /** The router `location.key` at which the substitution was first observed. */
  readonly locationKey: string;
  /** The course the link asked for, which this session cannot open. */
  readonly requested: string;
  /** The course actually shown instead. */
  readonly shown: string;
}

export function nextCourseSubstitution(
  current: CourseSubstitution | null,
  location: { key: string; navigationType: 'PUSH' | 'POP' | 'REPLACE' },
  requestedCourseKey: string | null,
  courseKeyInForce: string,
): CourseSubstitution | null {
  if (requestedCourseKey && courseKeyInForce && requestedCourseKey !== courseKeyInForce) {
    return current && current.requested === requestedCourseKey && current.shown === courseKeyInForce
      ? current
      : { locationKey: location.key, requested: requestedCourseKey, shown: courseKeyInForce };
  }

  if (!current) {
    return null;
  }

  const sameNavigation = current.locationKey === location.key;
  const screenRewroteItsOwnUrl = location.navigationType === 'REPLACE' && current.shown === courseKeyInForce;
  return sameNavigation || screenRewroteItsOwnUrl ? current : null;
}
